import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../state/auth/AuthContext.jsx';
import api from '../../api/axiosConfig.js';
import { getTenantFromBrowser } from '../../utils/tenant.js';
import { getLocationPath, getLoginLandingRoute } from '../../utils/loginLanding.js';

const MAX_ATTEMPTS = 5;
const COOLDOWN_DURATION = 30 * 1000;
const REMEMBER_KEY = 'ssv3_user';
const COOLDOWN_KEY = 'ssv3_cooldown';

const tips = [
  'Prioridad automática para las cargas urgentes. Configurá SLA y olvidate.',
  'Visualizá el estado de tu flota en tiempo real con dashboards dinámicos.',
  'Compartí notificaciones inteligentes con tu equipo y clientes en un clic.',
  'Reducí tiempos muertos asignando operadores disponibles con IA asistida.',
  'Sincronizá tus comprobantes y remitos con un único flujo simplificado.',
];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export default function useLoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [storedName, setStoredName] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const tick = setInterval(() => {
      setNow(new Date());
    }, 60 * 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      if (saved) {
        setUsername(saved);
        setStoredName(saved);
        setRememberMe(true);
      }
      const savedCooldown = localStorage.getItem(COOLDOWN_KEY);
      if (savedCooldown) {
        const parsed = Number(savedCooldown);
        if (!Number.isNaN(parsed) && parsed > Date.now()) {
          setCooldownUntil(parsed);
        } else {
          localStorage.removeItem(COOLDOWN_KEY);
        }
      }
    } catch (error) {
      console.warn('No se pudo acceder al almacenamiento local', error);
    }
  }, []);

  useEffect(() => {
    if (!cooldownUntil) return undefined;
    const interval = setInterval(() => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null);
        setAttempts(0);
        localStorage.removeItem(COOLDOWN_KEY);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const tipOfDay = useMemo(() => {
    const dayIndex = Math.floor(now.getTime() / (24 * 60 * 60 * 1000));
    return tips[dayIndex % tips.length];
  }, [now]);

  const greeting = useMemo(() => {
    const hour = now.getHours();
    const base = hour < 12 ? 'Buen día' : hour < 19 ? 'Buenas tardes' : 'Buenas noches';
    return storedName ? `${base}, ${storedName}` : `${base}!`;
  }, [now, storedName]);

  const formattedDate = useMemo(
    () =>
      new Intl.DateTimeFormat('es-AR', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }).format(now),
    [now],
  );

  const formattedTime = useMemo(
    () =>
      new Intl.DateTimeFormat('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(now),
    [now],
  );

  const redirectTo = getLocationPath(location.state?.from);

  const validate = useCallback(() => {
    const nextErrors = {};
    const trimmedUser = username.trim();
    if (!trimmedUser) {
      nextErrors.username = 'Ingresá tu usuario';
    } else if (trimmedUser.length < 2 || trimmedUser.length > 30) {
      nextErrors.username = 'El usuario debe tener entre 2 y 30 caracteres';
    }

    if (!password) {
      nextErrors.password = 'Ingresá tu contraseña';
    } else if (password.length < 4 || password.length > 64) {
      nextErrors.password = 'La contraseña debe tener entre 4 y 64 caracteres';
    }

    setErrors(nextErrors);
    return nextErrors;
  }, [password, username]);

  const showCooldownAlert = useCallback(until => {
    let timerId;
    Swal.fire({
      icon: 'warning',
      title: 'Ups',
      html: `Demasiados intentos. Esperá <b>${Math.max(1, Math.ceil((until - Date.now()) / 1000))}</b> segundos.`,
      confirmButtonColor: '#009688',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        const container = Swal.getHtmlContainer();
        const counter = container?.querySelector('b');
        timerId = setInterval(() => {
          const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));
          if (counter) counter.textContent = remaining.toString();
          if (remaining <= 0) {
            Swal.close();
          }
        }, 1000);
      },
      willClose: () => {
        if (timerId) clearInterval(timerId);
      },
    });
  }, []);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      if (loading) return;

      if (cooldownUntil && cooldownUntil > Date.now()) {
        setFeedback('error');
        showCooldownAlert(cooldownUntil);
        return;
      }

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setFeedback('error');
        return;
      }

      setLoading(true);
      setErrors({});
      setFeedback(null);

      try {
        // Obtener el tenant automáticamente
        const tenant = getTenantFromBrowser();

        // Hacer la petición de login a la API
        const response = await api.post('/auth/login', {
          email: username.trim(),
          password: password,
        }, {
          headers: {
            'x-tenant': tenant,
          },
        });

        const { token, user, tenant: tenantInfo } = response.data;

        login(user, { token, tenant: tenantInfo });

        // Guardar nombre de usuario si "recordarme" está activo
        if (rememberMe) {
          localStorage.setItem(REMEMBER_KEY, username.trim());
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }

        setStoredName(user.nombre || username.trim());
        setAttempts(0);
        localStorage.removeItem(COOLDOWN_KEY);
        setFeedback('success');

        await Swal.fire({
          icon: 'success',
          title: '¡Bienvenido!',
          text: `Hola ${user.nombre || user.email}`,
          showConfirmButton: false,
          timer: 1000,
        });

        navigate(redirectTo || getLoginLandingRoute(user, tenantInfo), { replace: true });
      } catch (error) {
        console.error('Error en login:', error);

        const nextAttempts = attempts + 1;

        // Obtener mensaje de error de la API
        const errorMessage = error.response?.data?.message || 'Usuario o contraseña inválidos';

        if (nextAttempts >= MAX_ATTEMPTS) {
          const until = Date.now() + COOLDOWN_DURATION;
          setCooldownUntil(until);
          setAttempts(0);
          localStorage.setItem(COOLDOWN_KEY, String(until));
          setErrors(prev => ({ ...prev, password: 'Esperá unos segundos para volver a intentar' }));
          setFeedback('error');
          showCooldownAlert(until);
        } else {
          setAttempts(nextAttempts);
          setErrors(prev => ({ ...prev, password: 'Revisá tus credenciales' }));
          setFeedback('error');
          await Swal.fire({
            icon: 'error',
            title: 'Error de autenticación',
            text: errorMessage,
            confirmButtonColor: '#009688',
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [
      attempts,
      cooldownUntil,
      loading,
      login,
      navigate,
      password,
      redirectTo,
      rememberMe,
      showCooldownAlert,
      username,
      validate,
    ],
  );

  const handleUsernameChange = useCallback(event => {
    setUsername(event.target.value);
    setErrors(prev => ({ ...prev, username: undefined }));
  }, []);

  const handlePasswordChange = useCallback(event => {
    setPassword(event.target.value);
    setErrors(prev => ({ ...prev, password: undefined }));
  }, []);

  const handleRememberChange = useCallback(event => {
    const checked = event.target.checked;
    setRememberMe(checked);
    if (!checked) {
      localStorage.removeItem(REMEMBER_KEY);
      setStoredName('');
    }
  }, []);

  const handlePasswordKeyEvent = useCallback(event => {
    if (event.getModifierState) {
      setCapsOn(event.getModifierState('CapsLock'));
    }
  }, []);

  const handlePasswordBlur = useCallback(() => {
    setCapsOn(false);
  }, []);

  const toggleShowPassword = useCallback(() => {
    setShowPass(prev => !prev);
  }, []);

  const acknowledgeFeedback = useCallback(() => {
    setFeedback(null);
  }, []);

  return {
    username,
    password,
    rememberMe,
    showPass,
    capsOn,
    errors,
    loading,
    greeting,
    tipOfDay,
    formattedDate,
    formattedTime,
    cooldownUntil,
    feedback,
    handleSubmit,
    handleUsernameChange,
    handlePasswordChange,
    handleRememberChange,
    handlePasswordKeyEvent,
    handlePasswordBlur,
    toggleShowPassword,
    acknowledgeFeedback,
  };
}
